import {useEffect, useRef, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faClosedCaptioning, faExpand, faFileLines, faPause, faVolumeHigh} from '@fortawesome/free-solid-svg-icons';

type Phase = 'idle' | 'scan' | 'detect' | 'fly' | 'land';

type Source = 'text' | 'speech';

type Line = {text: string; source: Source};

type Cue = Line & {id: number; start: number};

const LINES: Line[] = [
    {text: 'Have you seen the northern lights?', source: 'text'},
    {text: '你怎么知道我在五星市场啊', source: 'speech'},
    {text: '¿Cuándo llegaste a la ciudad?', source: 'text'},
    {text: '明日、駅で待ち合わせしよう', source: 'speech'},
    {text: 'Wir sehen uns morgen früh.', source: 'text'},
    {text: '그건 정말 오래된 이야기예요', source: 'speech'},
];

const PHASE_MS: Record<Phase, number> = {idle: 700, scan: 900, detect: 1150, fly: 700, land: 900};
const NEXT_PHASE: Record<Phase, Phase> = {idle: 'scan', scan: 'detect', detect: 'fly', fly: 'land', land: 'idle'};

const FIRST_CUE_AT = 12.4;
const CUE_GAP = 3.4;
const CUE_DURATION = 2.6;
const VIDEO_SECONDS = 165;
const VISIBLE_ROWS = 2;
const ROW_HEIGHT = 56;
const MAX_STORED_CUES = 40;

const pendingRowClasses = (phase: Phase) => {
    if (phase === 'detect') return {meta: 'motion-safe:animate-hero-fade', text: 'opacity-0', skeleton: 'motion-safe:animate-hero-fade'};
    if (phase === 'fly') return {meta: '', text: 'opacity-0', skeleton: 'motion-safe:animate-hero-skeleton-out'};
    if (phase === 'land') return {meta: '', text: 'motion-safe:animate-hero-ink', skeleton: 'motion-safe:animate-hero-skeleton-out'};
    return {meta: '', text: '', skeleton: ''};
};

const cueAt = (index: number): Cue => ({
    id: index + 1,
    start: FIRST_CUE_AT + index * CUE_GAP,
    ...LINES[index % LINES.length],
});

const pad = (n: number) => String(n).padStart(2, '0');

const srtTime = (seconds: number) => {
    const whole = Math.floor(seconds);
    const ms = Math.round((seconds - whole) * 1000);
    return `${pad(Math.floor(whole / 3600))}:${pad(Math.floor((whole % 3600) / 60))}:${pad(whole % 60)},${String(ms).padStart(3, '0')}`;
};

const clockTime = (seconds: number) => `${pad(Math.floor(seconds / 60))}:${pad(Math.floor(seconds % 60))}`;

const speechChunks = (text: string) =>
    text.includes(' ') ? text.split(' ') : (text.match(/.{1,2}/g) ?? [text]);

type Props = {
    label: string;
    frameSrc?: string;
};

export default function HeroAnimation({label, frameSrc}: Props) {
    const [phase, setPhase] = useState<Phase>('detect');
    const [current, setCurrent] = useState(2);
    const [cues, setCues] = useState<Cue[]>([cueAt(0), cueAt(1), cueAt(2)]);
    const [snapList, setSnapList] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);

    const rootRef = useRef<HTMLDivElement>(null);
    const subtitleRef = useRef<HTMLSpanElement>(null);
    const targetRef = useRef<HTMLDivElement>(null);
    const ghostRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        const sync = () => setReducedMotion(query.matches);
        sync();
        query.addEventListener('change', sync);
        return () => query.removeEventListener('change', sync);
    }, []);

    useEffect(() => {
        if (reducedMotion) return;

        const timer = window.setTimeout(() => {
            if (phase === 'scan') {
                setCues(list => [...list, cueAt(current)]);
            }
            if (phase === 'land') {
                setCurrent(index => index + 1);
                setCues(list => {
                    if (list.length <= MAX_STORED_CUES) return list;
                    setSnapList(true);
                    return list.slice(-VISIBLE_ROWS);
                });
            }
            if (phase === 'idle') {
                setSnapList(false);
            }
            setPhase(NEXT_PHASE[phase]);
        }, PHASE_MS[phase]);

        return () => window.clearTimeout(timer);
    }, [phase, current, reducedMotion]);

    useEffect(() => {
        if (phase !== 'fly') return;
        const ghost = ghostRef.current;
        const root = rootRef.current;
        const from = subtitleRef.current;
        const to = targetRef.current;
        if (!ghost || !root || !from || !to) return;

        const rootBox = root.getBoundingClientRect();
        const fromBox = from.getBoundingClientRect();
        const toBox = to.getBoundingClientRect();

        ghost.style.left = `${fromBox.left - rootBox.left}px`;
        ghost.style.top = `${fromBox.top - rootBox.top}px`;
        ghost.style.transform = 'translate(0, 0) scale(1)';
        ghost.style.opacity = '1';
        ghost.getBoundingClientRect();

        const scale = parseFloat(getComputedStyle(to).fontSize) / parseFloat(getComputedStyle(ghost).fontSize);
        const dx = toBox.left - fromBox.left;
        const dy = toBox.top + toBox.height / 2 - (fromBox.top + (fromBox.height * scale) / 2);

        const frame = requestAnimationFrame(() => {
            ghost.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
            ghost.style.color = 'rgb(249 115 22)';
            ghost.style.textShadow = 'none';
        });

        return () => cancelAnimationFrame(frame);
    }, [phase]);

    const line = LINES[current % LINES.length];
    const isSpeech = line.source === 'speech';
    const playhead = cueAt(current).start % VIDEO_SECONDS;
    const progress = (playhead / VIDEO_SECONDS) * 100;
    const listOffset = Math.max(0, cues.length - VISIBLE_ROWS) * ROW_HEIGHT;
    const subtitleHidden = phase === 'fly' || phase === 'land';
    const recognized = phase === 'detect' || phase === 'fly';

    return (
        <div ref={rootRef} dir="ltr" role="img" aria-label={label}
             className="relative w-full max-w-xl select-none pb-24 -mb-12 sm:mb-0 sm:pb-20">
            <div aria-hidden className="pointer-events-none absolute -inset-8 -z-10 rounded-[3rem] bg-gradient-to-tr from-orange-400/30 via-rose-400/10 to-indigo-500/30 blur-3xl dark:from-orange-500/20 dark:via-fuchsia-500/10 dark:to-indigo-500/25"/>

            <div aria-hidden className="relative overflow-hidden rounded-2xl bg-slate-950 shadow-2xl shadow-slate-900/30 ring-1 ring-black/10 dark:shadow-black/60 dark:ring-white/10">
                <div className="relative aspect-[2.2/1] overflow-hidden">
                    {frameSrc && (
                        <img src={frameSrc} alt="" className="absolute inset-0 h-full w-full origin-center object-cover motion-safe:animate-hero-pan"/>
                    )}

                    {phase === 'scan' && !isSpeech && (
                        <div className="absolute inset-0 motion-safe:animate-hero-scan bg-gradient-to-b from-transparent via-orange-400/10 to-orange-400/40">
                            <div className="absolute inset-x-0 bottom-0 h-0.5 bg-orange-400 shadow-[0_0_14px_3px_rgba(251,146,60,0.75)]"/>
                        </div>
                    )}

                    <div className="absolute inset-x-0 top-[8%] flex sm:top-auto sm:bottom-[18%] justify-center px-8">
                        <div className={`relative flex items-center gap-3 px-2 py-0.5 text-center text-sm font-medium text-white transition-opacity duration-300 [text-shadow:0_0_6px_rgba(0,0,0,0.9),0_1px_2px_rgba(0,0,0,1)] sm:text-base ${subtitleHidden ? 'opacity-0' : 'opacity-100'}`}>
                            {isSpeech ? (
                                <>
                                    <Waveform/>
                                    <span ref={subtitleRef} className={recognized ? '' : 'hidden'}>
                                        {recognized && speechChunks(line.text).map((chunk, index, all) => (
                                            <span key={index} className="inline-block motion-safe:animate-hero-cue-in"
                                                  style={{animationDelay: `${index * Math.min(140, 700 / all.length)}ms`}}>
                                                {index > 0 && line.text.includes(' ') ? ' ' : ''}{chunk}
                                            </span>
                                        ))}
                                    </span>
                                    {recognized && <SourceChip leaving={phase === 'fly'}>Speech</SourceChip>}
                                </>
                            ) : (
                                <>
                                    <span ref={subtitleRef}>{line.text}</span>
                                    {recognized && <DetectionBox leaving={phase === 'fly'}/>}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3 px-4 py-3 text-white/60">
                    <FontAwesomeIcon icon={faPause} className="h-3.5 w-3.5 text-white"/>
                    <div className="relative h-1 flex-1 rounded-full bg-white/15">
                        <div className="absolute inset-y-0 left-0 rounded-full bg-orange-500 transition-[width] duration-700 ease-linear"
                             style={{width: `${progress}%`}}/>
                        <div className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-500 ring-2 ring-slate-950 transition-[left] duration-700 ease-linear"
                             style={{left: `${progress}%`}}/>
                    </div>
                    <span className="font-mono text-[11px] tabular-nums text-white/80">
                        {clockTime(playhead)} / {clockTime(VIDEO_SECONDS)}
                    </span>
                    <FontAwesomeIcon icon={faVolumeHigh} className="hidden h-3.5 w-3.5 sm:block"/>
                    <FontAwesomeIcon icon={faClosedCaptioning} className="h-3.5 w-3.5 text-orange-400"/>
                    <FontAwesomeIcon icon={faExpand} className="hidden h-3.5 w-3.5 sm:block"/>
                </div>
            </div>

            <div aria-hidden className="absolute -right-3 bottom-[50px] w-[68%] sm:bottom-0 overflow-hidden rounded-xl bg-white shadow-xl shadow-slate-900/15 ring-1 ring-slate-900/10 dark:bg-slate-800 dark:shadow-black/50 dark:ring-white/10 sm:-right-4 sm:w-[52%]">
                <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 dark:border-slate-700">
                    <FontAwesomeIcon icon={faFileLines} className="h-3.5 w-3.5 text-orange-500"/>
                    <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">output.srt</span>
                </div>

                <div className="relative overflow-hidden" style={{height: VISIBLE_ROWS * ROW_HEIGHT}}>
                    <div className={snapList ? '' : 'transition-transform duration-500 ease-out'}
                         style={{transform: `translateY(-${listOffset}px)`}}>
                        {cues.map((cue, index) => {
                            const isPending = index === cues.length - 1;
                            const reveal = isPending ? pendingRowClasses(phase) : {meta: '', text: '', skeleton: ''};
                            return (
                                <div key={cue.id} className="flex items-start gap-3 px-4 py-2.5" style={{height: ROW_HEIGHT}}>
                                    <span className={`mt-0.5 w-5 shrink-0 font-mono text-[11px] text-slate-400 dark:text-slate-500 ${reveal.meta}`}>{cue.id}</span>
                                    <div className="min-w-0 flex-1">
                                        <div className={`truncate font-mono text-[10px] tabular-nums text-slate-400 dark:text-slate-500 ${reveal.meta}`}>
                                            {srtTime(cue.start)} --&gt; {srtTime(cue.start + CUE_DURATION)}
                                        </div>
                                        <div className="relative">
                                            <div ref={isPending ? targetRef : undefined}
                                                 className={`truncate text-[13px] font-medium text-slate-900 dark:text-slate-100 ${reveal.text}`}>
                                                {cue.text}
                                            </div>
                                            {isPending && reveal.skeleton && <Skeleton text={cue.text} className={reveal.skeleton}/>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {phase === 'fly' && (
                <div ref={ghostRef} aria-hidden
                     className="pointer-events-none absolute z-20 origin-top-left whitespace-nowrap text-sm font-medium text-white opacity-0 transition-[transform,color,text-shadow] duration-[650ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] [text-shadow:0_1px_3px_rgba(0,0,0,0.9)] sm:text-base">
                    {line.text}
                </div>
            )}
        </div>
    );
}

function Skeleton({text, className}: {text: string; className: string}) {
    const words = text.includes(' ')
        ? text.split(' ').map(word => word.length * 6.5)
        : (text.match(/.{1,3}/g) ?? [text]).map(chunk => chunk.length * 13);

    return (
        <div className={`absolute inset-0 flex items-center gap-1 overflow-hidden ${className}`}>
            {words.map((width, index) => (
                <span key={index} className="h-2.5 shrink-0 rounded-full bg-slate-100 motion-safe:animate-pulse dark:bg-slate-600"
                      style={{width}}/>
            ))}
        </div>
    );
}

function Waveform() {
    return (
        <span className="flex h-4 shrink-0 items-center gap-[3px]">
            {[0.5, 0.85, 1, 0.7, 0.45].map((height, index) => (
                <span key={index}
                      className="w-[3px] origin-center rounded-full bg-orange-400 motion-safe:animate-hero-wave"
                      style={{height: `${height * 100}%`, animationDelay: `${-index * 0.16}s`}}/>
            ))}
        </span>
    );
}

function SourceChip({leaving, delay = '0.4s', children}: {leaving: boolean; delay?: string; children: React.ReactNode}) {
    return (
        <span className={`absolute -top-3 left-0 hidden -translate-y-full transition-opacity duration-300 sm:block ${leaving ? 'opacity-0' : ''}`}>
            <span style={{animationDelay: delay}}
                  className="block rounded bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow [text-shadow:none] motion-safe:animate-hero-pop">
                {children}
            </span>
        </span>
    );
}

function DetectionBox({leaving}: {leaving: boolean}) {
    return (
        <div className={`absolute -inset-x-2.5 -inset-y-1.5 transition-[opacity,transform] duration-300 ease-out ${leaving ? 'scale-[1.03] opacity-0' : 'motion-safe:animate-hero-frame'}`}>
            <svg className="absolute inset-0 h-full w-full overflow-visible">
                <rect width="100%" height="100%" rx="4" fill="none" stroke="rgb(251 146 60)" strokeLinecap="round"
                      strokeWidth="1.5" pathLength={1} strokeDasharray={1} className="motion-safe:animate-hero-draw"/>
            </svg>
            <SourceChip leaving={false} delay="0.66s">Text</SourceChip>
        </div>
    );
}
